/**
 * What an organization is made of, in the words two different people use.
 *
 * Activist staff edit an organization from the staff area; a Super Admin edits
 * the same organization from their own Profile page. The two screens show
 * different subsets of the same record, so the option lists, the labels and the
 * field rules live here rather than inside either feature - a feature may not
 * import from another feature, and the same 56 state codes written down twice
 * drift apart within a week.
 *
 * The rules here mirror the backend's, rather than inventing a second set, so a
 * value is never accepted on a screen and refused by the server.
 */
import { z } from 'zod';

export const ORGANIZATION_TYPES = [
  { value: 'NONPROFIT', label: 'Nonprofit' },
  { value: 'ADVOCACY_GROUP', label: 'Advocacy group' },
  { value: 'LABOR_UNION', label: 'Labor union' },
  { value: 'POLITICAL_CAMPAIGN', label: 'Political campaign' },
  { value: 'PAC', label: 'PAC' },
  { value: 'COMMUNITY_GROUP', label: 'Community group' },
  { value: 'FAITH_BASED', label: 'Faith based' },
  { value: 'EDUCATIONAL', label: 'Educational' },
  { value: 'GOVERNMENT', label: 'Government' },
  { value: 'OTHER', label: 'Other' },
] as const;

export const GEOGRAPHIC_SCOPES = [
  { value: 'NATIONAL', label: 'National' },
  { value: 'MULTI_STATE', label: 'Multi-state' },
  { value: 'STATE', label: 'State' },
  { value: 'COUNTY', label: 'County' },
  { value: 'LOCAL', label: 'Local' },
] as const;

/** The 56 codes the backend accepts, which includes DC and the territories. */
export const STATE_CODES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'DC',
  'PR',
  'VI',
  'GU',
  'MP',
  'AS',
];

/** The scopes that make no sense without at least one state named. */
export const SCOPES_NEEDING_STATES = ['STATE', 'MULTI_STATE'];

/** True when a scope obliges the form to name states. */
export function scopeNeedsStates(scope: string | undefined): boolean {
  return SCOPES_NEEDING_STATES.includes(scope ?? '');
}

/**
 * The words and colours an organization's status and type are shown with.
 *
 * Shared between the list, the staff account page and the Super Admin's own
 * standing card, so the same status never reads one way in a row and another
 * way on the page that row leads to.
 */
export const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  ACTIVE: 'Active',
  SUSPENDED: 'Suspended',
  DEACTIVATED: 'Deactivated',
};

/** The same pairs as ORGANIZATION_TYPES, keyed for lookup; one list, not two. */
export const TYPE_LABELS: Record<string, string> = Object.fromEntries(
  ORGANIZATION_TYPES.map((type) => [type.value, type.label]),
);

/** Colour reinforces the status; the word carries it, as WCAG 2.1 AA requires. */
export const STATUS_COLOUR: Record<string, 'default' | 'success' | 'warning' | 'error'> =
  {
    PENDING: 'warning',
    ACTIVE: 'success',
    SUSPENDED: 'warning',
    DEACTIVATED: 'error',
  };

/** The two states in which the backend refuses every write to an organization. */
export function isWritable(status: string | undefined): boolean {
  return status !== 'SUSPENDED' && status !== 'DEACTIVATED';
}

export function formatDate(value: string) {
  return new Date(value).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * A calendar date - a contract's start, a renewal - as the reader writes dates.
 *
 * Not `formatDate`. The backend sends these as "2026-10-01", with no time, and
 * `new Date("2026-10-01")` reads that as midnight in London: anywhere west of
 * it the page would show 30 September. Built from its parts instead, the date
 * is the same day everywhere.
 */
export function formatDay(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return formatDate(value);
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString(
    undefined,
    { day: 'numeric', month: 'short', year: 'numeric' },
  );
}

/*
 * The commercial terms Activist Technologies records, in words.
 *
 * Here rather than in the staff billing form because the Super Admin's own
 * Billing page reads the same codes, and a section may not import from another.
 * One list per field, so a code never reads one way to staff and another way to
 * the organization it describes.
 */

export const BILLING_CYCLES = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'ANNUAL', label: 'Annual' },
  { value: 'CUSTOM', label: 'Custom' },
] as const;

export const INVOICE_STATUSES = [
  { value: 'NOT_INVOICED', label: 'Not invoiced' },
  { value: 'INVOICED', label: 'Invoiced' },
  { value: 'PAID', label: 'Paid' },
  { value: 'OVERDUE', label: 'Overdue' },
  { value: 'VOID', label: 'Void' },
] as const;

export const PAYMENT_STATUSES = [
  { value: 'UNPAID', label: 'Unpaid' },
  { value: 'PARTIAL', label: 'Part paid' },
  { value: 'PAID', label: 'Paid' },
  { value: 'OVERDUE', label: 'Overdue' },
  { value: 'REFUNDED', label: 'Refunded' },
  { value: 'WAIVED', label: 'Waived' },
] as const;

/** Anything shaped like the lists above. */
type Options = readonly { readonly value: string; readonly label: string }[];

/**
 * The words for a code, or the code itself when the list does not know it.
 *
 * A code the portal has not heard of is shown as it came rather than as a
 * blank. A blank reads as "nothing recorded", which would be untrue.
 */
export function labelFor(options: Options, code: string): string {
  return options.find((option) => option.value === code)?.label ?? code;
}

/**
 * An amount of money, as the reader writes numbers, with the currency code.
 *
 * "1500.00" and "USD" become "1,500.00 USD" for an American reader. The code
 * rather than a symbol, because "$" alone does not say which dollar. The amount
 * arrives as text with two decimals, the way the backend stores it, and a
 * currency code the runtime does not know throws - so the fallback is the two
 * parts side by side rather than a broken page.
 *
 * The number is formatted as currency, so it gets that currency's decimals and
 * the reader's separators, and the code is then put after it. English puts the
 * code first ("USD 1,500.00") and German after; always after reads the same way
 * on every screen.
 */
export function formatMoney(amount: string, currency: string): string {
  try {
    const number = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      currencyDisplay: 'code',
    })
      .formatToParts(Number(amount))
      .filter((part) => part.type !== 'currency' && part.type !== 'literal')
      .map((part) => part.value)
      .join('');
    return `${number} ${currency}`;
  } catch {
    return `${amount} ${currency}`;
  }
}

/** The six things an account's standing can be, in the words shown. */
export type AccountStanding =
  | 'Closed'
  | 'Suspended'
  | 'Awaiting activation'
  | 'Payment overdue'
  | 'Invoice outstanding'
  | 'In good standing';

/** Colour reinforces the standing; the words carry it. */
export const STANDING_COLOUR: Record<
  AccountStanding,
  'default' | 'success' | 'warning' | 'error'
> = {
  Closed: 'error',
  Suspended: 'warning',
  'Awaiting activation': 'warning',
  'Payment overdue': 'error',
  'Invoice outstanding': 'warning',
  'In good standing': 'success',
};

/**
 * An account's standing, in one short phrase.
 *
 * Nobody has defined "general account standing" for this project, so this is
 * the portal's rule, written down once. Earlier rows win:
 *
 * | Condition                                              | Phrase              |
 * | ------------------------------------------------------ | ------------------- |
 * | status is DEACTIVATED                                  | Closed              |
 * | status is SUSPENDED                                    | Suspended           |
 * | status is PENDING                                      | Awaiting activation |
 * | invoice or payment is OVERDUE                          | Payment overdue     |
 * | invoice is INVOICED, payment UNPAID or PARTIAL         | Invoice outstanding |
 * | otherwise, including neither status recorded           | In good standing    |
 *
 * The status comes first because a suspended account's standing is that it is
 * suspended, whatever the invoice says.
 */
export function accountStanding({
  status,
  invoice_status,
  payment_status,
}: {
  status: string;
  invoice_status?: string | null;
  payment_status?: string | null;
}): AccountStanding {
  if (status === 'DEACTIVATED') return 'Closed';
  if (status === 'SUSPENDED') return 'Suspended';
  if (status === 'PENDING') return 'Awaiting activation';
  if (invoice_status === 'OVERDUE' || payment_status === 'OVERDUE') {
    return 'Payment overdue';
  }
  if (
    invoice_status === 'INVOICED' &&
    (payment_status === 'UNPAID' || payment_status === 'PARTIAL')
  ) {
    return 'Invoice outstanding';
  }
  return 'In good standing';
}

/**
 * What each status means for the organization now, in the present tense.
 *
 * Not the staff status dialog's wording, which says what a change *will* do.
 * This says what the account *is*, to the people it belongs to.
 */
export const STATUS_MEANING: Record<string, string> = {
  PENDING:
    'Your account is waiting to be activated by Activist Technologies. It is not yet visible in the app.',
  ACTIVE:
    'Your organization is visible in the app, and its users can make changes in the portal.',
  SUSPENDED:
    'Your organization is hidden from the app and changes are not allowed. Contact Activist Technologies to discuss it.',
  DEACTIVATED:
    'Your account has ended. The organization is hidden from the app and changes are not allowed. Contact Activist Technologies to discuss it.',
};

/*
 * The field rules every organization form shares.
 *
 * Each is a maker rather than a constant, because Zod schemas carry their own
 * error messages and two forms occasionally want different words for the same
 * rule. Calling it also means no two forms share one schema object.
 */

/** Text that may be left blank. Blank arrives as undefined, never "". */
export const optionalText = () =>
  z
    .string()
    .trim()
    .optional()
    .transform((value) => (value === '' ? undefined : value));

/** A full web address, or nothing. */
export const websiteField = (
  message = 'Enter a full web address, starting with http:// or https://',
) =>
  z
    .string()
    .trim()
    .optional()
    .transform((value) => (value === '' ? undefined : value))
    .refine((value) => value === undefined || /^https?:\/\/.+\..+/.test(value), message);

/** An email address, or nothing. */
export const emailField = (message = 'Enter a valid email address') =>
  z
    .string()
    .trim()
    .optional()
    .transform((value) => (value === '' ? undefined : value))
    .refine(
      (value) => value === undefined || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value),
      message,
    );

/** The four parts of a contact, as a form holds them. */
export interface ContactParts {
  name?: string;
  title?: string;
  email?: string;
  phone?: string;
}

/**
 * Turn the four parts of a contact into what the API stores, or null.
 *
 * A contact is one JSON value on the record, so it is replaced whole. Every
 * part blank means there is no contact, which is a different claim from a
 * contact whose every detail happens to be blank - so it becomes null rather
 * than an object of empty strings.
 */
export function contactToPayload(parts: ContactParts): Record<string, string> | null {
  const filled = Object.entries(parts).filter(([, value]) => Boolean(value));
  return filled.length ? Object.fromEntries(filled) : null;
}

/**
 * A contact to send, with the address it was stored with.
 *
 * The backend replaces a contact whole rather than merging, and a contact can
 * hold an address no form here shows. Carried through as it was stored, or
 * saving a new phone number would quietly delete it. A contact emptied of every
 * part shown is still cleared, address and all - which is what "no contact"
 * means.
 */
export function keepStoredAddress(
  contact: Record<string, string> | null,
  stored: Record<string, unknown> | null | undefined,
): Record<string, string> | null {
  if (!contact) return null;
  const address = contactPart(stored, 'address');
  return address ? { ...contact, address } : contact;
}

/**
 * Read one part of a stored contact.
 *
 * The generated type leaves the three contacts as untyped objects, because the
 * backend stores them as JSON rather than columns. So every part is checked
 * before it is trusted to be text.
 */
export function contactPart(
  contact: Record<string, unknown> | null | undefined,
  part: string,
): string {
  const value = contact?.[part];
  return typeof value === 'string' ? value : '';
}
